import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GoBackButton from "components/project/GoBackButton";
import AdminFilterBar from "components/admin/AdminFilterBar";
import { getAdminProjects } from "lib/api/getAdminProjects";

const DEFAULT_QUERY = {
  keyword: "",
  searchField: "all",
  userFilter: "all",
  teamFilter: "all",
  sortKey: "createdAt",
  sortDir: "DESC",
  page: 1,
  limit: 20,
  startDate: null,
  endDate: null,
};

const SCROLL_LOAD_THRESHOLD = 320;

const QUERY_KEYS = [
  "keyword",
  "searchField",
  "userFilter",
  "teamFilter",
  "sortKey",
  "sortDir",
  "page",
  "limit",
  "startDate",
  "endDate",
];

const FILTER_QUERY_KEYS = QUERY_KEYS.filter((key) => key !== "page");

const SEARCH_FIELD_OPTIONS = [
  { value: "all", label: "통합 검색" },
  { value: "project", label: "프로젝트명" },
  { value: "user", label: "생성자" },
  { value: "team", label: "소속" },
];

function getComparableQueryValue(value) {
  if (value instanceof Date) return value.getTime();
  if (value == null) return "";
  return value;
}

function areQueriesEqual(left, right, keys = QUERY_KEYS) {
  return keys.every(
    (key) =>
      getComparableQueryValue(left?.[key]) ===
      getComparableQueryValue(right?.[key]),
  );
}

function getWindowScrollMetrics() {
  const doc = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
  const clientHeight = window.innerHeight || doc.clientHeight || 0;
  const scrollHeight = Math.max(
    doc.scrollHeight || 0,
    body.scrollHeight || 0,
    doc.offsetHeight || 0,
    body.offsetHeight || 0,
  );

  return {
    scrollTop,
    clientHeight,
    scrollHeight,
    distanceToBottom: scrollHeight - scrollTop - clientHeight,
  };
}

function getElementScrollMetrics(element) {
  return {
    scrollTop: element.scrollTop || 0,
    clientHeight: element.clientHeight || 0,
    scrollHeight: element.scrollHeight || 0,
    distanceToBottom:
      (element.scrollHeight || 0) -
      (element.scrollTop || 0) -
      (element.clientHeight || 0),
  };
}

export default function AdminProjectsPage() {
  const contentRootRef = useRef(null);
  const tableWrapRef = useRef(null);
  const isLoadingRef = useRef(false);
  const lastScrollTopMapRef = useRef(new Map());

  const [keyword, setKeyword] = useState(DEFAULT_QUERY.keyword);
  const [searchField, setSearchField] = useState(DEFAULT_QUERY.searchField);
  const [userFilter, setUserFilter] = useState(DEFAULT_QUERY.userFilter);
  const [teamFilter, setTeamFilter] = useState(DEFAULT_QUERY.teamFilter);
  const [sortKey, setSortKey] = useState(DEFAULT_QUERY.sortKey);
  const [sortDir, setSortDir] = useState(DEFAULT_QUERY.sortDir);
  const [limit, setLimit] = useState(DEFAULT_QUERY.limit);
  const [startDate, setStartDate] = useState(DEFAULT_QUERY.startDate);
  const [endDate, setEndDate] = useState(DEFAULT_QUERY.endDate);

  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [projects, setProjects] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ users: [], teams: [] });
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: DEFAULT_QUERY.limit,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadMoreMessage, setLoadMoreMessage] = useState("");

  const userOptions = useMemo(
    () => [...new Set(filterOptions.users ?? [])].sort(),
    [filterOptions.users],
  );
  const teamOptions = useMemo(
    () => [...new Set(filterOptions.teams ?? [])].sort(),
    [filterOptions.teams],
  );

  const searchPlaceholder = useMemo(() => {
    if (searchField === "project") return "프로젝트명으로 검색";
    if (searchField === "user") return "생성자 이름 검색";
    if (searchField === "team") return "소속명으로 검색";
    return "프로젝트명·생성자·소속 검색";
  }, [searchField]);

  const currentFilterQuery = useMemo(
    () => ({
      keyword,
      searchField,
      userFilter,
      teamFilter,
      sortKey,
      sortDir,
      limit,
      startDate,
      endDate,
      page: 1,
    }),
    [
      keyword,
      searchField,
      userFilter,
      teamFilter,
      sortKey,
      sortDir,
      limit,
      startDate,
      endDate,
    ],
  );

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setQuery((previous) =>
        areQueriesEqual(previous, currentFilterQuery)
          ? previous
          : currentFilterQuery,
      );
    }, 250);

    return () => window.clearTimeout(timerId);
  }, [currentFilterQuery]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      isLoadingRef.current = true;
      setLoadMoreMessage("");

      if (query.page === 1) {
        lastScrollTopMapRef.current = new Map();
        contentRootRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        tableWrapRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        setErrorMessage("");
        setProjects([]);
        setTotalCount(0);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          limit: query.limit,
        });
      }

      try {
        const result = await getAdminProjects(query);
        if (cancelled) return;

        if (!result?.success) {
          if (query.page === 1) {
            setProjects([]);
            setTotalCount(0);
            setPagination({
              currentPage: 1,
              totalPages: 1,
              limit: query.limit,
            });
            setErrorMessage(
              result?.message || "프로젝트 목록을 불러오지 못했습니다.",
            );
          } else {
            setLoadMoreMessage(
              result?.message || "프로젝트 목록을 불러오지 못했습니다.",
            );
          }
          return;
        }

        const nextRows = Array.isArray(result.rows) ? result.rows : [];
        setProjects((previousRows) => {
          if (query.page === 1) return nextRows;

          const seenIds = new Set(
            previousRows.map((row) => row?.id).filter(Boolean),
          );
          const uniqueNextRows = nextRows.filter((row) => {
            const rowId = row?.id;
            if (!rowId) return true;
            if (seenIds.has(rowId)) return false;
            seenIds.add(rowId);
            return true;
          });

          return [...previousRows, ...uniqueNextRows];
        });
        setTotalCount(Number(result.totalCount ?? 0));
        setPagination(
          result.pagination || {
            currentPage: query.page,
            totalPages: query.page,
            limit: query.limit,
          },
        );
        setFilterOptions(result.filterOptions ?? { users: [], teams: [] });
      } catch (error) {
        if (cancelled) return;
        if (query.page === 1) {
          setProjects([]);
          setTotalCount(0);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            limit: query.limit,
          });
          setErrorMessage(
            error?.message || "프로젝트 목록을 불러오지 못했습니다.",
          );
        } else {
          setLoadMoreMessage(
            error?.message || "프로젝트 목록을 불러오지 못했습니다.",
          );
        }
      } finally {
        if (!cancelled) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [query]);

  const submitSearch = (e) => {
    e.preventDefault();
    setQuery((previous) =>
      areQueriesEqual(previous, currentFilterQuery)
        ? previous
        : currentFilterQuery,
    );
  };

  const clearAllFilters = () => {
    setKeyword(DEFAULT_QUERY.keyword);
    setSearchField(DEFAULT_QUERY.searchField);
    setUserFilter(DEFAULT_QUERY.userFilter);
    setTeamFilter(DEFAULT_QUERY.teamFilter);
    setSortKey(DEFAULT_QUERY.sortKey);
    setSortDir(DEFAULT_QUERY.sortDir);
    setLimit(DEFAULT_QUERY.limit);
    setStartDate(DEFAULT_QUERY.startDate);
    setEndDate(DEFAULT_QUERY.endDate);
    setQuery(DEFAULT_QUERY);
  };

  const currentPage = Number(pagination.currentPage || query.page || 1);
  const currentLimit = Number(
    pagination.limit || query.limit || DEFAULT_QUERY.limit,
  );
  const fallbackTotalPages =
    totalCount > 0 ? Math.max(1, Math.ceil(totalCount / currentLimit)) : 1;
  const totalPages = Number(pagination.totalPages || fallbackTotalPages || 1);
  const hasMore =
    currentPage < totalPages ||
    (totalCount > 0 && projects.length < totalCount);
  const isInitialLoading =
    isLoading && query.page === 1 && projects.length === 0;

  const loadNextPage = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;

    isLoadingRef.current = true;
    setQuery((previous) => {
      if (!areQueriesEqual(previous, currentFilterQuery, FILTER_QUERY_KEYS)) {
        return currentFilterQuery;
      }

      const activePage = Number(previous.page || pagination.currentPage || 1);
      const activeLimit = Number(
        pagination.limit || previous.limit || DEFAULT_QUERY.limit,
      );
      const fallbackTotalPages =
        totalCount > 0 ? Math.max(1, Math.ceil(totalCount / activeLimit)) : 1;
      const maxPage = Math.max(
        Number(pagination.totalPages || 1),
        fallbackTotalPages,
      );

      if (
        activePage >= maxPage ||
        (totalCount > 0 && projects.length >= totalCount)
      ) {
        isLoadingRef.current = false;
        return previous;
      }

      return {
        ...previous,
        page: activePage + 1,
      };
    });
  }, [
    currentFilterQuery,
    hasMore,
    pagination.currentPage,
    pagination.limit,
    pagination.totalPages,
    projects.length,
    totalCount,
  ]);

  const maybeLoadNextPageFromScroll = useCallback(
    (source) => {
      if (!source || !hasMore || isLoadingRef.current) return;

      const metrics =
        source === window
          ? getWindowScrollMetrics()
          : getElementScrollMetrics(source);
      const previousTop = lastScrollTopMapRef.current.get(source) ?? 0;
      lastScrollTopMapRef.current.set(source, metrics.scrollTop);

      const canScroll = metrics.scrollHeight > metrics.clientHeight + 1;
      const isScrollingDown = metrics.scrollTop > previousTop;

      if (!canScroll || !isScrollingDown || metrics.scrollTop <= 0) return;

      if (metrics.distanceToBottom <= SCROLL_LOAD_THRESHOLD) {
        loadNextPage();
      }
    },
    [hasMore, loadNextPage],
  );

  const handleContentScroll = useCallback(
    (event) => {
      maybeLoadNextPageFromScroll(event.currentTarget);
    },
    [maybeLoadNextPageFromScroll],
  );

  useEffect(() => {
    if (!hasMore) return undefined;

    const root = contentRootRef.current;
    const tableWrap = tableWrapRef.current;
    const handleRootScroll = () => maybeLoadNextPageFromScroll(root);
    const handleTableWrapScroll = () => maybeLoadNextPageFromScroll(tableWrap);
    const handleWindowScroll = () => maybeLoadNextPageFromScroll(window);

    root?.addEventListener("scroll", handleRootScroll, { passive: true });
    tableWrap?.addEventListener("scroll", handleTableWrapScroll, {
      passive: true,
    });
    window.addEventListener("scroll", handleWindowScroll, { passive: true });

    return () => {
      root?.removeEventListener("scroll", handleRootScroll);
      tableWrap?.removeEventListener("scroll", handleTableWrapScroll);
      window.removeEventListener("scroll", handleWindowScroll);
    };
  }, [hasMore, maybeLoadNextPageFromScroll, projects.length]);

  return (
    <section
      className="content admin adminDetailPage kpiProjectDetail"
      ref={contentRootRef}
      onScroll={handleContentScroll}
    >
      <div className="helpTitleRow">
        <GoBackButton index />
        <h2>프로젝트 관리</h2>
      </div>

      <AdminFilterBar
        keyword={keyword}
        onChangeKeyword={setKeyword}
        keywordPlaceholder={searchPlaceholder}
        searchField={searchField}
        onChangeSearchField={setSearchField}
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        extraFilters={[
          {
            key: "userFilter",
            value: userFilter,
            onChange: setUserFilter,
            options: [
              { value: "all", label: "전체 사용자" },
              ...userOptions.map((u) => ({ value: u, label: u })),
            ],
          },
          {
            key: "teamFilter",
            value: teamFilter,
            onChange: setTeamFilter,
            options: [
              { value: "all", label: "전체 소속" },
              ...teamOptions.map((t) => ({ value: t, label: t })),
            ],
          },
          {
            key: "sortKey",
            value: sortKey,
            onChange: setSortKey,
            options: [
              { value: "createdAt", label: "생성일" },
              { value: "projectName", label: "프로젝트명" },
              { value: "user", label: "생성자" },
              { value: "totalFiles", label: "전체 파일 수" },
            ],
          },
          {
            key: "sortDir",
            value: sortDir,
            onChange: setSortDir,
            options: [
              { value: "DESC", label: "내림차순" },
              { value: "ASC", label: "오름차순" },
            ],
          },
          {
            key: "limit",
            value: String(limit),
            onChange: (value) => setLimit(Number(value)),
            options: [
              { value: "10", label: "10개씩" },
              { value: "20", label: "20개씩" },
              { value: "50", label: "50개씩" },
            ],
          },
        ]}
        startDate={startDate}
        endDate={endDate}
        onChangeDateRange={(start, end) => {
          setStartDate(start);
          setEndDate(end);
        }}
        onSubmit={submitSearch}
        onResetAll={clearAllFilters}
        isLoading={isLoading}
        resultCount={totalCount}
      />

      <div
        className="card detailTableWrap"
        ref={tableWrapRef}
        onScroll={handleContentScroll}
      >
        <table className="detailTable projectKpiTable">
          <thead>
            <tr>
              <th>생성일</th>
              <th>프로젝트명</th>
              <th>생성자</th>
              <th>소속</th>
              <th>이미지 파일</th>
              <th>동영상 파일</th>
              <th>전기설비</th>
              <th>가스설비</th>
              <th>기계설비</th>
              <th>전체 파일 수</th>
            </tr>
          </thead>
          <tbody>
            {errorMessage ? (
              <tr>
                <td colSpan={10} className="emptyCell">
                  {errorMessage}
                </td>
              </tr>
            ) : isInitialLoading ? (
              <tr>
                <td colSpan={10} className="emptyCell">
                  프로젝트 목록을 불러오는 중입니다.
                </td>
              </tr>
            ) : projects.length === 0 ? (
              <tr>
                <td colSpan={10} className="emptyCell">
                  조건에 맞는 프로젝트가 없습니다.
                </td>
              </tr>
            ) : (
              projects.map((p) => (
                <tr key={p.id}>
                  <td>{p.createdAt}</td>
                  <td>{p.projectName}</td>
                  <td>{p.user}</td>
                  <td>{p.team}</td>
                  <td>{p.imageCount}</td>
                  <td>{p.videoCount}</td>
                  <td>{p.elecCount}</td>
                  <td>{p.gasCount}</td>
                  <td>{p.machineCount}</td>
                  <td>{p.totalFiles}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="adminFilePagination">
        {isLoading && projects.length > 0 ? (
          <span>프로젝트 목록을 불러오는 중입니다.</span>
        ) : loadMoreMessage ? (
          <span>{loadMoreMessage}</span>
        ) : hasMore ? (
          <span>아래로 스크롤하면 더 불러옵니다.</span>
        ) : projects.length > 0 ? (
          <span>마지막 목록입니다.</span>
        ) : null}
      </div>
    </section>
  );
}
